import React, { useState, useEffect, useRef, useMemo } from 'react';
import './AdminUsers.css';
import './InstructorStudents.css';
import { apiFetch } from './api';
import { isReadOnly } from './academicYear';
import { useActiveCourse } from './ActiveCourseContext';

const AVATAR_PALETTE = [
  ['#4CAF50', '#2e7d32'],
  ['#3b82f6', '#1d4ed8'],
  ['#8b5cf6', '#6d28d9'],
  ['#ec4899', '#be185d'],
  ['#f59e0b', '#b45309'],
  ['#06b6d4', '#0e7490'],
  ['#ef4444', '#b91c1c'],
  ['#14b8a6', '#0f766e'],
];

function getInitials(name) {
  if (!name) return '?';
  const cleaned = name.replace(/\b(Prof|Doç|Dr|Öğr|Gör|Üyesi)\.?/gi, '').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getAvatarColors(seed) {
  const key = String(seed ?? '');
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  const idx = Math.abs(hash) % AVATAR_PALETTE.length;
  const [from, to] = AVATAR_PALETTE[idx];
  return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
}

function StudentDetailPage({ student, studentCourses, isAdmin, onBack, handleResetPassword, handleImpersonate, resettingPassword, impersonatingUser, resetMessage }) {
  const [courseData, setCourseData] = useState({});
  const [globalLoading, setGlobalLoading] = useState(true);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setGlobalLoading(true);
    try {
      const [activeRes, pastRes] = await Promise.all([
        apiFetch('/instructor/exams'),
        apiFetch('/instructor/exams?archived=true'),
      ]);
      const [activeData, pastData] = await Promise.all([activeRes.json(), pastRes.json()]);
      const allExams = [...(activeData.exams || []), ...(pastData.exams || [])];

      const updates = {};
      for (let i = 0; i < studentCourses.length; i++) {
        const { courseId } = studentCourses[i];
        const key = `${courseId}-${i}`;
        updates[key] = { loading: true, exams: [] };
      }
      setCourseData({ ...updates });
      setGlobalLoading(false);

      for (let i = 0; i < studentCourses.length; i++) {
        const { courseId } = studentCourses[i];
        const key = `${courseId}-${i}`;
        const courseExams = allExams.filter((e) => e.course_id === courseId);

        if (courseExams.length === 0) {
          setCourseData((prev) => ({ ...prev, [key]: { loading: false, exams: [] } }));
          continue;
        }

        const exams = [];
        for (const exam of courseExams) {
          try {
            const sRes = await apiFetch(`/instructor/exams/${exam.exam_id}/students`);
            const sData = await sRes.json();
            const found = (sData.students || []).find((s) => s.student_number === student.student_number);
            exams.push({
              exam_id: exam.exam_id,
              exam_title: exam.title,
              attempt_status: found?.attempt_status || null,
              total_points: found?.total_points ?? null,
            });
          } catch {
            exams.push({ exam_id: exam.exam_id, exam_title: exam.title, attempt_status: null, total_points: null });
          }
        }
        setCourseData((prev) => ({ ...prev, [key]: { loading: false, exams } }));
      }
    } catch {
      setGlobalLoading(false);
    }
  };

  const statusBadge = (s) => {
    if (s === 'Submitted') return { text: 'Teslim Edildi', cls: 'submitted' };
    if (s === 'InProgress') return { text: 'Devam Ediyor', cls: 'in-progress' };
    return { text: 'Girmedi', cls: 'absent' };
  };

  return (
    <div className="is-detail-page">
      <div className="is-detail-header">
        <button className="is-back-btn" onClick={onBack}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Geri
        </button>
        <div className="is-detail-identity">
          <div className="is-detail-avatar" style={{ background: getAvatarColors(student.student_id || student.student_number) }}>
            {getInitials(student.name)}
          </div>
          <div className="is-detail-name">
            <h2>{student.name || '-'}</h2>
            <span className="is-detail-name-sub">{student.student_number || '—'}</span>
          </div>
        </div>
        {isAdmin && student.student_id && (
          <div className="is-detail-actions">
            <button
              onClick={(e) => handleImpersonate(e, student.student_id)}
              disabled={impersonatingUser === student.student_id}
              className="au-btn au-btn-impersonate"
              title="Bu öğrencinin hesabına geçiş yap"
            >
              {impersonatingUser === student.student_id ? (
                <>
                  <span className="au-btn-spin" />
                  <span className="au-btn-text">Açılıyor</span>
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                    <polyline points="10 17 15 12 10 7" />
                    <line x1="15" y1="12" x2="3" y2="12" />
                  </svg>
                  <span className="au-btn-text">Hesaba Gir</span>
                </>
              )}
            </button>
            <button
              onClick={(e) => handleResetPassword(e, student.student_id, student.name)}
              disabled={resettingPassword === student.student_id}
              className="au-btn au-btn-reset"
              title="Şifreyi sıfırla"
            >
              {resettingPassword === student.student_id ? (
                <>
                  <span className="au-btn-spin" />
                  <span className="au-btn-text">Sıfırlanıyor</span>
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 2v6h-6" />
                    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                    <path d="M3 22v-6h6" />
                    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                  </svg>
                  <span className="au-btn-text">Şifre Sıfırla</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {resetMessage?.text && (
        <div className={`au-flash ${resetMessage.type === 'success' ? 'success' : 'error'}`}>
          {resetMessage.type === 'success' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
          {resetMessage.text}
        </div>
      )}

      <div className="is-info-grid">
        <div className="is-info-card">
          <span className="is-info-label">Öğrenci No</span>
          <span className="is-info-value">{student.student_number || '-'}</span>
        </div>
        <div className="is-info-card">
          <span className="is-info-label">Program</span>
          <span className="is-info-value">{student.program || '-'}</span>
        </div>
      </div>

      {globalLoading ? (
        <div className="au-card">
          <div className="au-skeleton">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="au-skeleton-row">
                <div className="au-skeleton-block" style={{ width: '30%' }} />
                <div className="au-skeleton-block" style={{ width: 80, height: 20 }} />
                <div className="au-skeleton-block" style={{ width: 60, height: 20 }} />
              </div>
            ))}
          </div>
        </div>
      ) : studentCourses.length === 0 ? (
        <div className="au-card">
          <div className="au-empty">
            <div className="au-empty-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </div>
            <div className="au-empty-text">Bu öğrenciye ait ders bulunamadı</div>
          </div>
        </div>
      ) : (
        <div className="is-courses-stack">
          {studentCourses.map(({ name, courseId }, i) => {
            const key = `${courseId}-${i}`;
            const cd = courseData[key];
            return (
              <div key={key} className="is-course-card">
                <div className="is-course-card-header">
                  <span className="is-course-card-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                  </span>
                  <div className="is-course-card-title">{name}</div>
                </div>
                <div className={`is-course-card-body ${!cd || cd.loading ? 'loading' : cd.exams.length === 0 ? 'empty' : ''}`}>
                  {!cd || cd.loading ? (
                    <>
                      <span className="au-btn-spin" style={{ color: '#94a3b8' }} />
                      Yükleniyor...
                    </>
                  ) : cd.exams.length === 0 ? (
                    'Bu ders için sınav bulunamadı.'
                  ) : (
                    <table className="is-exam-table">
                      <thead>
                        <tr>
                          <th>Sınav</th>
                          <th className="center">Durum</th>
                          <th className="right">Puan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cd.exams.map((g) => {
                          const st = statusBadge(g.attempt_status);
                          return (
                            <tr key={g.exam_id}>
                              <td>{g.exam_title}</td>
                              <td className="center">
                                <span className={`is-status-badge ${st.cls}`}>{st.text}</span>
                              </td>
                              <td className={`right is-score-cell ${g.total_points == null ? 'empty' : ''}`}>
                                {g.total_points != null ? Number(g.total_points).toFixed(2) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InstructorStudents({ instructorId, isAdmin = false, onImpersonate }) {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [courseSearch, setCourseSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [resettingPassword, setResettingPassword] = useState(null);
  const [resetMessage, setResetMessage] = useState({ type: '', text: '' });
  const [impersonatingUser, setImpersonatingUser] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [archiveReadOnly, setArchiveReadOnly] = useState(isReadOnly());
  const activeCourseContext = useActiveCourse();
  const studentsRequestRef = useRef(0);

  useEffect(() => {
    const updateYear = (event) => setArchiveReadOnly(Boolean(event.detail && !event.detail.editable));
    window.addEventListener('academic-year-meta-changed', updateYear);
    return () => window.removeEventListener('academic-year-meta-changed', updateYear);
  }, []);

  useEffect(() => {
    if (instructorId !== null && instructorId !== undefined) {
      fetchCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructorId]);

  useEffect(() => {
    if (instructorId === null || instructorId === undefined) return;
    // Ders değişirken önce önceki dersin listesini kaldır. Birden fazla istek
    // çakışırsa fetchStudents yalnızca en son seçime ait yanıtı uygular.
    setStudents([]);
    setFilteredStudents([]);
    fetchStudents(false);
    setSelectedStudent(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourse, instructorId]);

  useEffect(() => {
    if (!activeCourseContext) return;
    const nextCourseId = activeCourseContext.activeCourseId || 'all';
    if (selectedCourse !== nextCourseId) setSelectedCourse(nextCourseId);
  }, [activeCourseContext, selectedCourse]);

  useEffect(() => {
    if (instructorId === null || instructorId === undefined) return;
    const interval = setInterval(() => {
      fetchCourses(true).catch(() => {});
      fetchStudents(true).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructorId, selectedCourse]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredStudents(students);
    } else {
      const q = searchTerm.toLocaleLowerCase('tr');
      setFilteredStudents(
        students.filter(
          (s) =>
            (s.name || '').toLocaleLowerCase('tr').includes(q) ||
            (s.student_number || '').toLocaleLowerCase('tr').includes(q)
        )
      );
    }
  }, [searchTerm, students]);

  const getCourseLabel = (course) => {
    const code = (course.course_code || '').trim().toUpperCase() || null;
    const name = course.course_name || `Ders ${course.course_id}`;
    const parts = [];
    if (course.program_name) parts.push(course.program_name);
    if (course.faculty_name) {
      const fn = course.faculty_name.trim();
      parts.push(/^myo\s*:/i.test(fn) ? fn : `MYO: ${fn}`);
    }
    const detail = parts.length ? ` (${parts.join(' - ')})` : '';
    return code ? `${code} — ${name}${detail}` : `${name}${detail}`;
  };

  const filteredCourses = useMemo(
    () => courses.filter((course) => {
      if (!courseSearch) return true;
      return getCourseLabel(course).toLocaleLowerCase('tr').includes(courseSearch.toLocaleLowerCase('tr'));
    }),
    [courses, courseSearch]
  );

  const selectedLabel =
    selectedCourse === 'all'
      ? 'Tüm Dersler'
      : (() => {
          const c = courses.find((c) => String(c.course_id) === selectedCourse);
          return c ? getCourseLabel(c) : 'Tüm Dersler';
        })();

  const handleSelectCourse = (value) => {
    setSelectedCourse(value);
    setDropdownOpen(false);
    setCourseSearch('');
    if (activeCourseContext) activeCourseContext.setActiveCourseId(value === 'all' ? '' : value);
  };

  const parseStudentCourses = (student) => {
    if (selectedCourse !== 'all') {
      return [{ name: student.course_name || '', courseId: parseInt(selectedCourse, 10) }];
    }
    if (!student.course_name) return [];
    const names = student.course_name.split(', ');
    if (student.course_ids) {
      const ids = student.course_ids.split(',').map((id) => parseInt(id.trim(), 10));
      return names.map((name, i) => ({ name: name.trim(), courseId: ids[i] || null }));
    }
    return names.map((name) => {
      const found = courses.find((c) => c.course_name === name.trim());
      return { name: name.trim(), courseId: found?.course_id || null };
    });
  };

  const handleStudentClick = (student) => {
    setSelectedStudent({ student, courses: parseStudentCourses(student) });
  };

  const handleUpload = async () => {
    if (!uploadFile) { setUploadError('Lütfen bir dosya seçin'); return; }
    if (selectedCourse === 'all') { setUploadError('Lütfen bir ders seçin'); return; }
    const course = courses.find((c) => String(c.course_id) === selectedCourse);
    if (!course) { setUploadError('Ders bulunamadı'); return; }
    setUploading(true);
    setUploadError('');
    setUploadMessage('');
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('course_id', course.course_id);
    formData.append('program_id', course.program_id || '');
    formData.append('faculty_id', course.faculty_id || '');
    try {
      const response = await apiFetch('/upload-student-list', { method: 'POST', body: formData });
      const data = await response.json();
      if (data.success) {
        setUploadMessage(`${data.inserted} öğrenci eklendi, ${data.skipped} atlandı.`);
        setUploadFile(null);
        fetchStudents(true);
      } else {
        setUploadError(data.error || 'Yükleme başarısız');
      }
    } catch (err) {
      setUploadError('Bağlantı hatası: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (selectedCourse === 'all') { setDeleteError('Lütfen bir ders seçin'); return; }
    if (!window.confirm('Bu derse ait tüm öğrenci listesi silinecek. Emin misiniz?')) return;
    setDeleting(true);
    setDeleteError('');
    setDeleteMessage('');
    try {
      const response = await apiFetch(`/student-list/${selectedCourse}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        setDeleteMessage(`${data.deleted} öğrenci silindi.`);
        fetchStudents(true);
      } else {
        setDeleteError(data.error || 'Silme başarısız');
      }
    } catch (err) {
      setDeleteError('Bağlantı hatası: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async (e, studentId, studentName) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`${studentName || 'Bu öğrenci'}nin şifresini sıfırlamak istediğinize emin misiniz?`)) return;
    setResettingPassword(studentId);
    setResetMessage({ type: '', text: '' });
    try {
      const response = await apiFetch(`/admin/users/${studentId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ user_type: 'student' }),
      });
      const data = await response.json();
      if (data.success) {
        setResetMessage({ type: 'success', text: data.message });
        setTimeout(() => setResetMessage({ type: '', text: '' }), 5000);
      } else {
        setResetMessage({ type: 'error', text: data.message || 'Şifre sıfırlanamadı' });
      }
    } catch {
      setResetMessage({ type: 'error', text: 'Şifre sıfırlanırken bir hata oluştu' });
    } finally {
      setResettingPassword(null);
    }
  };

  const handleImpersonate = async (e, studentId) => {
    if (e) e.stopPropagation();
    if (!onImpersonate) return;

    setImpersonatingUser(studentId);
    setResetMessage({ type: '', text: '' });

    try {
      const response = await apiFetch('/admin/impersonate', {
        method: 'POST',
        body: JSON.stringify({ user_id: studentId, user_type: 'student' }),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        onImpersonate({
          userType: data.user.userType,
          user: data.user,
          token: data.token,
          impersonation: data.impersonation,
        });
      } else {
        setResetMessage({ type: 'error', text: data.message || 'Hesaba giriş yapılamadı' });
      }
    } catch {
      setResetMessage({ type: 'error', text: 'Hesaba geçiş sırasında bir hata oluştu' });
    } finally {
      setImpersonatingUser(null);
    }
  };

  const fetchCourses = async (silent = false) => {
    try {
      const response = await apiFetch(`/instructor/courses`);
      const data = await response.json();
      if (data.success) setCourses(data.courses || []);
    } catch (err) {
      if (!silent) console.error('Dersler yüklenemedi:', err);
    }
  };

  const fetchStudents = async (silent = false) => {
    const requestId = ++studentsRequestRef.current;
    try {
      if (!silent) setLoading(true);
      if (!silent) setError('');
      let url = `/instructor/students`;
      if (selectedCourse && selectedCourse !== 'all') {
        const courseId = parseInt(selectedCourse, 10);
        if (!isNaN(courseId)) url += `?course_id=${courseId}`;
      }
      const response = await apiFetch(url);
      const data = await response.json();
      if (requestId !== studentsRequestRef.current) return;
      if (data.success) {
        const sorted = (data.students || []).slice().sort((a, b) => (a.student_id ?? 0) - (b.student_id ?? 0));
        setStudents(sorted);
        setFilteredStudents(sorted);
      } else {
        if (!silent) setError(data.message || 'Öğrenciler yüklenemedi');
      }
    } catch {
      if (requestId === studentsRequestRef.current && !silent) setError('Bağlantı hatası');
    } finally {
      if (requestId === studentsRequestRef.current) setLoading(false);
    }
  };

  if (selectedStudent) {
    return (
      <StudentDetailPage
        student={selectedStudent.student}
        studentCourses={selectedStudent.courses}
        isAdmin={isAdmin && !archiveReadOnly}
        onBack={() => setSelectedStudent(null)}
        handleResetPassword={handleResetPassword}
        handleImpersonate={handleImpersonate}
        resettingPassword={resettingPassword}
        impersonatingUser={impersonatingUser}
        resetMessage={resetMessage}
      />
    );
  }

  if (error) {
    return (
      <div className="au-page">
        <div className="au-toolbar">
          <h2 className="au-title">Öğrenciler</h2>
        </div>
        <div className="au-flash error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="au-page">
      <div className="au-toolbar">
        <div className="au-toolbar-top">
          <div className="au-title-block">
            <h2 className="au-title">Öğrenciler</h2>
            <p className="au-subtitle">
              {isAdmin
                ? 'Öğrenci listelerini yönetin, hesaplara geçiş yapın ve şifre sıfırlayın.'
                : 'Derslerinize kayıtlı öğrencileri görüntüleyin ve liste yükleyin.'}
            </p>
          </div>
          <div className="au-count-pill">
            <span className="au-count-pill-dot" />
            {filteredStudents.length} öğrenci
            {searchTerm && students.length !== filteredStudents.length && (
              <span style={{ marginLeft: 4, color: '#64748b', fontWeight: 500 }}>/ {students.length}</span>
            )}
          </div>
        </div>

        <div className="is-toolbar-row">
          {!activeCourseContext && (
          <div className="is-course-filter" ref={dropdownRef}>
            <button
              type="button"
              className={`is-course-btn ${dropdownOpen ? 'open' : ''}`}
              onClick={() => { setDropdownOpen((o) => !o); setCourseSearch(''); }}
            >
              <span className="is-course-btn-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </span>
              <span className="is-course-btn-label">{selectedLabel}</span>
              <span className="is-course-btn-chevron">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>
            {dropdownOpen && (
              <div className="is-course-panel">
                <div className="is-course-search-wrap">
                  <span className="is-course-search-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </span>
                  <input
                    autoFocus
                    type="text"
                    className="is-course-search-input"
                    placeholder="Ders kodu veya adı ara..."
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                  />
                </div>
                <ul className="is-course-list">
                  <li
                    className={`is-course-item ${selectedCourse === 'all' ? 'selected' : ''}`}
                    onClick={() => handleSelectCourse('all')}
                  >
                    Tüm Dersler
                    {selectedCourse === 'all' && (
                      <span className="is-course-item-check">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                    )}
                  </li>
                  {filteredCourses.length === 0 ? (
                    <li className="is-course-empty">Sonuç bulunamadı</li>
                  ) : (
                    filteredCourses.map((course, index) => {
                      const isSel = selectedCourse === String(course.course_id);
                      return (
                        <li
                          key={`course-${course.course_id}-${index}`}
                          className={`is-course-item ${isSel ? 'selected' : ''}`}
                          onClick={() => handleSelectCourse(String(course.course_id))}
                        >
                          {getCourseLabel(course)}
                          {isSel && (
                            <span className="is-course-item-check">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </span>
                          )}
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
            )}
          </div>
          )}

          <div className="au-search-wrapper" style={{ flex: 1 }}>
            <span className="au-search-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              type="text"
              className="au-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Ad soyad veya öğrenci no ara..."
            />
            {searchTerm && (
              <button
                type="button"
                className="au-search-clear"
                onClick={() => setSearchTerm('')}
                aria-label="Aramayı temizle"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {selectedCourse !== 'all' && !archiveReadOnly && (
        <div className="is-upload-card">
          <span className="is-upload-title">Sınıf Listesi</span>
          <label className="is-file-label">
            <input
              type="file"
              accept=".xls,.xlsx"
              className="is-file-input"
              onChange={(e) => { setUploadFile(e.target.files[0]); setUploadMessage(''); setUploadError(''); }}
            />
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            Dosya Seç
          </label>
          <span className={`is-file-name ${uploadFile ? '' : 'placeholder'}`}>
            {uploadFile ? uploadFile.name : 'Dosya seçilmedi'}
          </span>
          <button
            className="is-action-btn primary"
            onClick={handleUpload}
            disabled={uploading || !uploadFile}
          >
            {uploading ? (
              <><span className="au-btn-spin" />Yükleniyor</>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Listeyi Yükle
              </>
            )}
          </button>
          <button
            className="is-action-btn danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? (
              <><span className="au-btn-spin" />Siliniyor</>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                </svg>
                Listeyi Sil
              </>
            )}
          </button>
          {uploadMessage && (
            <span className="is-upload-feedback success">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {uploadMessage}
            </span>
          )}
          {uploadError && (
            <span className="is-upload-feedback error">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
              </svg>
              {uploadError}
            </span>
          )}
          {deleteMessage && (
            <span className="is-upload-feedback success">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {deleteMessage}
            </span>
          )}
          {deleteError && (
            <span className="is-upload-feedback error">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
              </svg>
              {deleteError}
            </span>
          )}
        </div>
      )}

      {resetMessage.text && (
        <div className={`au-flash ${resetMessage.type === 'success' ? 'success' : 'error'}`}>
          {resetMessage.type === 'success' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
          {resetMessage.text}
        </div>
      )}

      <div className="au-card">
        <div className="au-table-wrap">
          {loading ? (
            <div className="au-skeleton">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="au-skeleton-row">
                  <div className="au-skeleton-circle" />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div className="au-skeleton-block" style={{ width: '40%' }} />
                    <div className="au-skeleton-block" style={{ width: '25%', height: 11 }} />
                  </div>
                  <div className="au-skeleton-block" style={{ width: 120, height: 14 }} />
                  <div className="au-skeleton-block" style={{ width: 160, height: 14 }} />
                  {isAdmin && !archiveReadOnly && <div className="au-skeleton-block" style={{ width: 180, height: 32 }} />}
                </div>
              ))}
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="au-empty">
              <div className="au-empty-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="au-empty-text">
                {searchTerm
                  ? 'Aramaya uygun öğrenci bulunamadı'
                  : selectedCourse === 'all'
                  ? (archiveReadOnly ? 'Bu arşiv yılında öğrenci bulunmamaktadır' : 'Bu akademik yıl için öğrenci listesi yükleyin')
                  : (archiveReadOnly ? 'Bu derste arşiv öğrencisi bulunmamaktadır' : 'Bu ders için öğrenci listesi yükleyin')}
              </div>
              {searchTerm && (
                <div className="au-empty-sub">"{searchTerm}" için sonuç yok. Aramayı değiştirmeyi deneyin.</div>
              )}
            </div>
          ) : (
            <table className="au-table is-students-table">
              <thead>
                <tr>
                  <th className="col-student">Öğrenci</th>
                  <th className="col-no">Öğrenci No</th>
                  <th className="col-program">Program</th>
                  <th className="col-course">Ders</th>
                  {isAdmin && !archiveReadOnly && <th className="right col-actions">İşlemler</th>}
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student, index) => (
                  <tr
                    key={`${student.student_id}-${index}`}
                    className="is-clickable-row"
                    onClick={() => handleStudentClick(student)}
                  >
                    <td className="col-student">
                      <div className="au-user-cell">
                        <div className="au-avatar" style={{ background: getAvatarColors(student.student_id || student.student_number) }}>
                          {getInitials(student.name)}
                        </div>
                        <div className="au-user-info">
                          <span className="au-user-name">{student.name || '-'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="col-no">
                      <span className="is-no-badge">{student.student_number || '—'}</span>
                    </td>
                    <td className="col-program is-program-cell" title={student.program || ''}>
                      {student.program || '-'}
                    </td>
                    <td className="col-course is-course-cell" title={student.course_name || ''}>
                      {student.course_name || '-'}
                    </td>
                    {isAdmin && !archiveReadOnly && (
                      <td className="col-actions">
                        {student.student_id ? (
                          <div className="au-actions">
                            <button
                              onClick={(e) => handleImpersonate(e, student.student_id)}
                              disabled={impersonatingUser === student.student_id}
                              className="au-btn au-btn-impersonate"
                              title="Bu öğrencinin hesabına geçiş yap"
                            >
                              {impersonatingUser === student.student_id ? (
                                <><span className="au-btn-spin" /><span className="au-btn-text">Açılıyor</span></>
                              ) : (
                                <>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                                    <polyline points="10 17 15 12 10 7" />
                                    <line x1="15" y1="12" x2="3" y2="12" />
                                  </svg>
                                  <span className="au-btn-text">Hesaba Gir</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={(e) => handleResetPassword(e, student.student_id, student.name)}
                              disabled={resettingPassword === student.student_id}
                              className="au-btn au-btn-reset"
                              title="Şifreyi sıfırla"
                            >
                              {resettingPassword === student.student_id ? (
                                <><span className="au-btn-spin" /><span className="au-btn-text">Sıfırlanıyor</span></>
                              ) : (
                                <>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 2v6h-6" />
                                    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                                    <path d="M3 22v-6h6" />
                                    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                                  </svg>
                                  <span className="au-btn-text">Şifre Sıfırla</span>
                                </>
                              )}
                            </button>
                          </div>
                        ) : (
                          <span className="is-no-account">Hesap yok</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default InstructorStudents;
